import { useEffect, useState, type RefObject } from 'react'
import {
  computeTabStripMaxWidthAgainstChecklist,
  shouldScrollApplicationTabsWithChecklist,
} from '../utils/checklistTabStripWidth'
import type { ChecklistAwareTabStripLayout } from '../types/hooks'

/**
 * When the underwrite checklist is open, sizes the Application detail tab strip
 * against the fixed panel and switches to horizontal scroll below 1250px.
 */
export function useChecklistAwareTabStripWidth(
  stripRef: RefObject<HTMLElement | null>,
  checklistOpen: boolean,
): ChecklistAwareTabStripLayout {
  const [maxWidth, setMaxWidth] = useState<number | undefined>(undefined)
  const [scrollTabs, setScrollTabs] = useState(false)

  useEffect(() => {
    if (!checklistOpen) {
      setMaxWidth(undefined)
      setScrollTabs(false)
      return
    }

    const update = () => {
      const viewportWidth = window.innerWidth
      setScrollTabs(shouldScrollApplicationTabsWithChecklist(true, viewportWidth))

      const el = stripRef.current
      if (!el) return
      setMaxWidth(
        computeTabStripMaxWidthAgainstChecklist({
          stripLeft: el.getBoundingClientRect().left,
          viewportWidth,
        }),
      )
    }

    update()
    window.addEventListener('resize', update)

    const el = stripRef.current
    const observer = new ResizeObserver(update)
    if (el) {
      observer.observe(el)
      if (el.parentElement) observer.observe(el.parentElement)
    }

    return () => {
      window.removeEventListener('resize', update)
      observer.disconnect()
    }
  }, [checklistOpen, stripRef])

  return { maxWidth, scrollTabs }
}
